/**
 * Strip inline styles and formatting from pasted HTML.
 * Preserves text content and basic structure (paragraphs, line breaks).
 * Exported for testing purposes.
 *
 * `keepSectionHeading` is the killswitch state, passed in rather than read here
 * so this stays pure and so both halves flip together — see the `transformPasted`
 * note in `./index`.
 */
export const sanitizePastedHtml = (
  html: string,
  { keepSectionHeading = true }: { keepSectionHeading?: boolean } = {}
): string => {
  // Parse in an inert document (no browsing context) so pasted markup can't
  // execute during sanitization (e.g. `<img onerror>`) — mirrors ProseMirror's
  // own clipboard parsing. Not an XSS sanitizer: `<script>`, `on*` handlers and
  // `javascript:` hrefs pass through and are neutralized downstream by
  // ProseMirror's schema parse.
  const container = document.implementation.createHTMLDocument('').createElement('div');
  container.innerHTML = html;

  // Remove all style attributes from all elements
  const elementsWithStyle = container.querySelectorAll('[style]');
  elementsWithStyle.forEach((el) => el.removeAttribute('style'));

  // Remove class attributes that might carry styling
  const elementsWithClass = container.querySelectorAll('[class]');
  elementsWithClass.forEach((el) => el.removeAttribute('class'));

  // Unwrap spans that now have no semantic purpose (only used for styling)
  const spans = container.querySelectorAll('span');
  spans.forEach((span) => {
    // If span has no attributes left, unwrap it (replace with its text content)
    if (span.attributes.length === 0) {
      const parent = span.parentNode;
      while (span.firstChild) {
        parent?.insertBefore(span.firstChild, span);
      }
      parent?.removeChild(span);
    }
  });

  // Remove font tags (legacy formatting)
  const fonts = container.querySelectorAll('font');
  fonts.forEach((font) => {
    const parent = font.parentNode;
    while (font.firstChild) {
      parent?.insertBefore(font.firstChild, font);
    }
    parent?.removeChild(font);
  });

  // Unwrap heading tags into paragraphs to prevent external paste from
  // introducing heading nodes outside the section parser pipeline. `h1` is the
  // section delimiter, so it is kept for `normalizePastedSections` to rebuild a
  // section from; unwrapping it strands the title as body text.
  const headings = container.querySelectorAll(
    keepSectionHeading ? 'h2, h3, h4, h5, h6' : 'h1, h2, h3, h4, h5, h6'
  );
  headings.forEach((heading) => {
    const p = document.createElement('p');
    while (heading.firstChild) {
      p.appendChild(heading.firstChild);
    }
    heading.parentNode?.replaceChild(p, heading);
  });

  return container.innerHTML;
};
